import { AxiosRequestConfig } from "axios";
import { useStore } from "../../../../Store";
import {
  Consent,
  DataHolderBrand,
  SoftwareProduct,
  SoftwareProductWording,
} from "../../../../Types/AppData";
import Helpers from "../../../Functions/Helpers";

export type ManagePortalData = {
  consentId: string;
  dataHolderLogoUri: string;
  dataHolderDisplayName: string;
  consentGrantedDate: string;
  status: string;
};

export function useGetManagePortalData(
  setManagePortalDataList: (data: ManagePortalData[]) => void,
  onError: (message: string) => void,
  customerId?: string
) {
  const { token, setLoading } = useStore();

  const config: AxiosRequestConfig = {
    method: "get",
    url:
      process.env.REACT_APP_PUBLIC_API_BASE_URI +
      "/portal/manage/data/" +
      customerId,
    headers: {
      Authorization: "Bearer " + token?.access_token,
    },
  };

  function GetData() {
    if (!customerId) return;

    setLoading(true);
    Helpers.CallApi(
      config,
      (response) => {
        // handle success
        setManagePortalDataList(response?.data?.consents);
        setLoading(false);
      },
      (message) => {
        // handle error
        setLoading(false);
        onError(message);
      }
    );
  }

  return GetData;
}

export type IndividualConsentData = {
  consent: {
    consent: Consent;
    dataHolderBrand: DataHolderBrand;
  };
  softwareProduct: { softwareProduct: SoftwareProduct };
  softwareProductWording: { softwareProductWording: SoftwareProductWording };
};

export function useGetIndividualConsentData(
  setIndividualConsentData: (data?: IndividualConsentData) => void,
  onError: (message: string) => void,
  consentId?: string
) {
  const { token, setLoading } = useStore();

  const config: AxiosRequestConfig = {
    method: "get",
    url:
      process.env.REACT_APP_PUBLIC_API_BASE_URI +
      "/portal/manage/consent/" +
      consentId,
    headers: {
      Authorization: "Bearer " + token?.access_token,
    },
  };

  function GetData() {
    if (!consentId) return;

    setLoading(true);
    Helpers.CallApi(
      config,
      (response) => {
        // handle success
        setIndividualConsentData(response?.data?.data);
        setLoading(false);
      },
      (message) => {
        // handle error
        setLoading(false);
        onError(message);
      }
    );
  }

  return GetData;
}

export function useUpdateConsent(
  onSuccess: () => void,
  onError: (message: string) => void
) {
  const { token, setLoading } = useStore();

  function onUpdateConsentDeleteMyData(consentId?: string, consent?: Consent) {
    if (!consent || !consentId) return;

    const checked = !Boolean(consent.deleteMyData);
    consent.deleteMyData = checked;
    UpdateConsent(consentId!, consent);
  }

  function UpdateConsent(consentId: string, consentData: Consent) {
    const configPut: AxiosRequestConfig = {
      method: "put",
      url: process.env.REACT_APP_PUBLIC_API_BASE_URI + "/consent/" + consentId,
      headers: {
        Authorization: "Bearer " + token?.access_token,
      },
      data: consentData,
    };

    Helpers.CallApi(configPut, onSuccess, (message) => {
      // handle error
      onError(message);
    });
  }

  return {
    updateConsentMutation: UpdateConsent,
    deleteMyDataConsentMutation: onUpdateConsentDeleteMyData,
  };
}

export function useWithdrawConsent(
  onSuccess: () => void,
  onError: (message: string) => void
) {
  const { token, setLoading } = useStore();

  function WithdrawConsent(consent?: Consent) {
    if (!consent) {
      throw new Error("Cannot withdraw consent without a valid Id in the URL.");
    }

    // update consent object first
    let configPost: AxiosRequestConfig = {
      method: "post",
      url:
        process.env.REACT_APP_PUBLIC_API_BASE_URI +
        "/consent/" +
        consent.id +
        "/withdraw",
      headers: {
        Authorization: "Bearer " + token?.access_token,
      },
    };

    Helpers.CallApi(configPost, onSuccess, (message) => {
      // handle error
      onError(message);
    });
  }
  return WithdrawConsent;
}
